import { useContext, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@brainfinance/icash-component-library"

import { InstantLoanContext } from "../../../Helpers/Reducers/InstantLoan"
import { postMessageSender } from "../../../Helpers/PostMessage"
import { goToIdentityVerificationSDK } from "../../../Helpers/Routes"

import faceIdIcon from "../../../images/identity-verification/face-id.svg"
import documentIcon from "../../../images/identity-verification/document.svg"
import cameraIcon from "../../../images/identity-verification/camera.svg"
import checkIcon from "../../../images/identity-verification/check.svg"
import questionsIcon from "../../../images/identity-verification/questions.svg"
import chevronIcon from "../../../images/ic-chevron-left.svg"

export function IdentityVerification() {
  const [isExpanded, setIsExpanded] = useState(false)
  const navigate = useNavigate()

  const {
    state: { isApp, workflowType },
  } = useContext(InstantLoanContext)

  const status: 'identityVerificationWithFaceRecognition' | 'documentOnly' =
    workflowType === 'document_and_biometric'
      ? 'identityVerificationWithFaceRecognition'
      : 'documentOnly'

  const handleContinue = () => {
    postMessageSender({
      event: "marketing",
      payload: {
        status: "track",
        data: {
          event: "app_identity_verification_continue_clicked",
        },
      },
    })
    goToIdentityVerificationSDK(navigate)
  }

  return (
    <div className="max-w-[622px] sm:w-full">
      {/* Face ID Icon */}
      <div className="flex justify-center mb-[32px]">
        <img src={faceIdIcon} alt="Identity verification" className="h-[82px] w-[82px]" />
      </div>

      {/* Title */}
      <h2 className="caption-light-no-color !text-[#13141A] !font-[600] !mb-[16px] !text-center sm:!text-left">
        {status === "identityVerificationWithFaceRecognition"
          ? "Let's verify your identity:"
          : "Verify your identity document:"}
      </h2>

      {/* Bullet List */}
      <ul className="list-disc list-inside mb-[24px]">
        <li className="caption-medium">
          {status === "identityVerificationWithFaceRecognition"
            ? "To keep your account secure"
            : "To confirm your account secure"}
        </li>
        <li className="caption-medium">To complete your application process</li>
        <li className="caption-medium">It takes less than 2 minutes</li>
        <li className="caption-medium">Bank-level security</li>
        <li className="caption-medium">Your privacy protected</li>
      </ul>

      {/* What to expect Card */}
      <div className="bg-[#F7F7F7] rounded-[16px] p-[16px] mb-[24px]">
        <h3 className="caption-light-no-color !text-[#13141A] !font-[600] !mb-[16px]">
          What to expect
        </h3>

        {/* Step 1 */}
        <div className="flex items-start gap-[16px] mb-[16px]">
          <div className="bg-white rounded-full w-[44px] h-[44px] flex items-center justify-center flex-shrink-0">
            <img src={documentIcon} alt="Document" />
          </div>
          <div>
            <p className="caption-light-no-color !text-[#13141A] !font-[600] !mb-[4px]">
              {status === "identityVerificationWithFaceRecognition"
                ? "Capture your ID"
                : "Prepare your ID"}
            </p>
            <p className="caption-medium">
              {status === "identityVerificationWithFaceRecognition"
                ? "Take photos of a government-issued photo ID (driver's license, passport, or national ID)"
                : "Have your driver's license, passport, or government-issued photo ID ready"}
            </p>
          </div>
        </div>

        {/* Step 2 */}
        <div className="flex items-start gap-[16px] mb-[16px] sm:gap-[12px]">
          <div className="bg-white rounded-full w-[44px] h-[44px] flex items-center justify-center flex-shrink-0">
            <img src={cameraIcon} alt="Camera" />
          </div>
          <div>
            <p className="caption-light-no-color !text-[#13141A] !font-[600] !mb-[4px]">
              {status === "identityVerificationWithFaceRecognition"
                ? "Take a quick selfie"
                : "Capture clear photos"}
            </p>
            <p className="caption-medium">
              {status === "identityVerificationWithFaceRecognition"
                ? "We'll verify that you're the person on the ID with a simple selfie"
                : "Take photos of your ID in good lighting with all details visible"}
            </p>
          </div>
        </div>

        {/* Step 3 */}
        <div className="flex items-start gap-[16px] sm:gap-[12px]">
          <div className="bg-white rounded-full w-[44px] h-[44px] flex items-center justify-center flex-shrink-0">
            <img src={checkIcon} alt="Check" />
          </div>
          <div>
            <p className="caption-light-no-color !text-[#13141A] !font-[600] !mb-[4px]">
              {status === "identityVerificationWithFaceRecognition"
                ? "You're all set"
                : "Submit and continue"}
            </p>
            <p className="caption-medium">
              {status === "identityVerificationWithFaceRecognition"
                ? "Once verified, we'll continue processing your application"
                : "Once submitted, we'll verify your document and continue processing your application"}
            </p>
          </div>
        </div>
      </div>

      {/* Expandable FAQ Section */}
      <div className="border border-[#E4E4E4] rounded-[24px] p-[16px] mb-[22px]">
        <div
          className="flex items-center cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <img src={questionsIcon} alt="Question" className="w-[30px] h-[30px] mr-[12px]" />
          <span className="caption-light-no-color !text-[#13141A] !font-[600] !flex-1">
            {status === "identityVerificationWithFaceRecognition"
              ? "Why do we need to verify your identity?"
              : "What documents can I use?"}
          </span>
          <img
            src={chevronIcon}
            alt="Toggle"
            className={`w-[24px] h-[24px] transition-transform duration-200 ${
              isExpanded ? "rotate-[90deg]" : "-rotate-90"
            }`}
          />
        </div>

        {isExpanded && (
          <div className="mt-[16px]">
            {status === "identityVerificationWithFaceRecognition" ? (
              <>
                <p className="caption-medium">
                  Identity verification is a standard security measure that helps us:
                </p>
                <ul className="list-disc list-inside">
                  <li className="caption-medium">
                    Protect your account from unauthorized access and identity theft
                  </li>
                  <li className="caption-medium">
                    Meet regulatory requirements and industry standards
                  </li>
                  <li className="caption-medium">
                    Ensure the security of your personal and financial information
                  </li>
                  <li className="caption-medium">
                    Provide you with a safe and trustworthy service
                  </li>
                </ul>
              </>
            ) : (
              <>
                <p className="caption-medium">
                  You can use any of the following government-issued photo IDs:
                </p>
                <ul className="list-disc list-inside">
                  <li className="caption-medium">Driver's license (front and back)</li>
                  <li className="caption-medium">Passport (photo page)</li>
                  <li className="caption-medium">National ID card</li>
                  <li className="caption-medium">State or provincial ID card</li>
                </ul>
                <p className="caption-medium !mt-[16px]">
                  Make sure your ID is current and not expired.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Continue Button */}
      <Button
        size="large"
        className="!w-full sm:!py-[13px] sm:!text-[16px] !mb-[22px]"
        appearance="primary"
        onClick={handleContinue}
      >
        Continue
      </Button>

      {/* Privacy Text */}
      <p className="caption">
        By clicking Continue, you agree that Entrust Corporation, the provider of Entrust Identity Verification (formerly Onfido), 
        may collect, store, use, and share your biometric and other sensitive personal information for identity verification and 
        fraud prevention purposes as described in its{" "}
        <span
          onClick={() => {
            const url = "https://www.entrust.com/sites/default/files/documentation/licensingandagreements/product-privacy-notice-identity-verification-services.pdf"
            if (!isApp) {
              window.open(url, "_blank")
            } else {
              postMessageSender({
                event: "display-website",
                payload: { url },
              })
            }
          }}
          className="text-primary cursor-pointer"
        >
          Privacy Notice
        </span>
        .
      </p>
    </div>
  )
}
